import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import {
  MAX_SECTION_LABEL_LENGTH,
  formatSection,
  normalizeSectionLabel,
  normalizeSectionNumbering,
  sponsorPrizeModeOf,
  normalizeSponsorPrizeSwitches,
} from "score-common";
import { TableSkeleton, useActiveOrg, useActiveShow, userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useOrgMembers } from "../hooks/useOrgMembers";
import RingsAndDaysSection from "../components/RingsAndDaysSection";
import ReconcileStatusSection, { FVD_SYNC_CARD_ID } from "../components/ReconcileStatusSection";
import { useCanAssignScribes } from "../hooks/useCanAssignScribes";
import { isLive } from "../util/live";

// #1026 — read-only Settings page for the selected show, reachable from the
// gear icon in the left nav (App.jsx navItems). Every field in the read-only
// InfoSections below comes straight off the score-show row (useActiveShow's
// activeShow, itself sourced from the generated score-common client via
// ScoreadminActiveShowProvider — no direct fetch, no new API call needed
// there since ActiveShowProvider already holds the full row).
//
// #1029 adds two live controls below the read-only sections:
//   - the per-show FVD-sync switch, MOVED here from ShowDashboard.jsx's old
//     NoFoxboxCard (#1015) — same flag/control, single home now, so it's
//     visible in every env (it's a real per-show operational switch, not a
//     demo-only feature).
//   - a demo Re-seed Show control (#1028's POST /demo/reseed), LIVE=false
//     only — see ReseedShowCard's header comment for why.
//
// The foxbox ringDayStructure display string is no longer shown (#3652): the
// "Rings and days" table is built from the show's classes
// (components/RingsAndDaysSection.jsx). The field stays on the row.
// managerName/secretaryName (#1034) remain unwired — foxbox doesn't know the real FVD
// .sdf column name yet (needs an on-prem probe; see mapShow's header comment)
// — so they still render as a dash until that's confirmed, same as
// usefCompId did before #1004/#1005 wired it up.

// Parses a bare YYYY-MM-DD (score-show's startDate/endDate carry no time
// component) into a LOCAL calendar date, never through `new Date(isoString)`
// — that treats a date-only string as UTC midnight, which toLocaleDateString
// can then roll back a day in a western timezone. There's no time-of-day here
// to get wrong, just a date to not shift.
function formatShowDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function InfoRow({ label, value, testId }) {
  return (
    <Box sx={{ display: "flex", gap: 2, py: 0.75 }} data-testid={testId}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200 }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.primary">
        {value}
      </Typography>
    </Box>
  );
}

function InfoSection({ title, children, testId }) {
  return (
    <Paper sx={{ p: 2 }} data-testid={testId}>
      <Typography variant="h6" gutterBottom color="text.primary">
        {title}
      </Typography>
      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
        {children}
      </Stack>
    </Paper>
  );
}

// #1015 — per-show "do not interact with FoxBox" toggle, MOVED here from
// ShowDashboard.jsx's NoFoxboxCard by #1029 (single home; no longer rendered
// on the dashboard). Persisted server-side on the Show row (score-api's
// setShowNoFoxbox, RMW pattern) rather than an env var, so it's a runtime,
// per-show switch: flip it and the very next FoxBox pull for this show
// (apps/foxbox/src/write/pull.js's isReleased) treats every one of its
// released scores as writeBackBlocked and never writes them back to FVD.
// Read/ingest (fvd-ingest → reconcile) is unaffected on purpose — see
// show-config.js's header comment — this only ever gates the write-back leg,
// so a flagged demo/test show stays fully scorable, it just can't push
// anything back onto FVD. Visible in every env (prod included) — this is a
// real per-show operational control, not a demo-only feature, so it's not
// LIVE-gated the way ReseedShowCard below is.
function NoFoxboxCard({ showId, initialValue, scoreApi, t }) {
  const [checked, setChecked] = useState(initialValue === true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reseed from the server value whenever the active show changes (or its
  // stored value changes underneath us via the show switcher's own listShows
  // refresh) — never let a stale toggle from the PREVIOUS show bleed through.
  useEffect(() => {
    setChecked(initialValue === true);
    setError(null);
  }, [showId, initialValue]);

  const handleChange = async (event) => {
    const next = event.target.checked;
    setSaving(true);
    setError(null);
    const previous = checked;
    setChecked(next); // optimistic; reverts on failure below
    try {
      const updated = unwrap(await scoreApi.shows.setShowNoFoxbox(showId, { noFoxbox: next }));
      setChecked(updated.noFoxbox === true);
    } catch (err) {
      setChecked(previous);
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" id="show-config-card" data-testid="show-config-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("dashboard.config.title")}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              disabled={saving}
              onChange={handleChange}
              data-testid="no-foxbox-toggle"
              inputProps={{ "aria-label": t("dashboard.config.noFoxboxLabel") }}
            />
          }
          label={t("dashboard.config.noFoxboxLabel")}
        />
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.config.noFoxboxHelp")}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="no-foxbox-error">
            {t("dashboard.config.noFoxboxSaveError")}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// #629 — per-show scribe unlock PIN. Same RMW pattern/home as NoFoxboxCard
// above (score-api's show-config.js, setShowScribeUnlockPin), just a text
// field instead of a switch. Both scribe-fe's PinLock (ScribeLock) and
// ScribeResume unlock with this SAME PIN (the approved design: one PIN per
// show, not a separate resume PIN) — falling back to the legacy hardcoded
// "5432" when a show has none set, so leaving this blank never locks anyone
// out. A blank save clears the field server-side (null), reverting to that
// fallback.
const PIN_PATTERN = /^[0-9]{4,8}$/;

function ScribePinCard({ showId, initialValue, scoreApi, t }) {
  const [value, setValue] = useState(initialValue ?? "");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  useEffect(() => {
    setValue(initialValue ?? "");
    setFeedback(null);
  }, [showId, initialValue]);

  const digits = value.replace(/\D/g, "");
  const isValid = digits === "" || PIN_PATTERN.test(digits);

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    setFeedback(null);
    try {
      const updated = unwrap(
        await scoreApi.shows.setShowScribeUnlockPin(showId, {
          scribeUnlockPin: digits === "" ? null : digits,
        })
      );
      setValue(updated.scribeUnlockPin ?? "");
      setFeedback({ severity: "success", message: t("settings.scribePin.saveSuccess") });
    } catch {
      setFeedback({ severity: "error", message: t("settings.scribePin.saveError") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" id="scribe-pin-card" data-testid="scribe-pin-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.scribePin.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.scribePin.help")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <TextField
            size="small"
            label={t("settings.scribePin.label")}
            value={value}
            disabled={saving}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 8))}
            error={!isValid}
            helperText={!isValid ? t("settings.scribePin.invalid") : " "}
            inputProps={{
              "data-testid": "scribe-pin-input",
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
          <Button
            variant="contained"
            disabled={saving || !isValid}
            onClick={handleSave}
            data-testid="scribe-pin-save-button"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t("settings.scribePin.save")}
          </Button>
        </Stack>
        <Snackbar
          open={feedback != null}
          // Success auto-dismisses; an error stays until the user closes it —
          // a validation error the user never got to read is worse than a
          // lingering toast, and the 6s auto-hide also raced Cypress under CI
          // load (the error toast vanished before the assertion polled it),
          // flaking the prod deploy. #1126.
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={
                feedback.severity === "success" ? "scribe-pin-success" : "scribe-pin-error"
              }
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// #1904 — per-show friendly short label (score-api's setShowFriendlyName,
// Show.friendlyName). The app bar's ShowSwitcher renders it instead of the FVD
// show name; blank clears it (null) and the full name is shown again. Same
// Save-button shape as ScribePinCard above. The cap mirrors score-api's
// MAX_SHOW_FRIENDLY_NAME_LENGTH: the label has to fit the switcher whole.
const MAX_SHOW_FRIENDLY_NAME_LENGTH = 30;

function FriendlyNameCard({ showId, initialValue, scoreApi, onSaved, t }) {
  const [value, setValue] = useState(initialValue ?? "");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  // Feedback resets only on a show switch: a save pushes the new value back
  // through `initialValue` (via patchShow), which must not clear the success
  // toast it just raised.
  useEffect(() => {
    setValue(initialValue ?? "");
  }, [initialValue]);
  useEffect(() => {
    setFeedback(null);
  }, [showId]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const trimmed = value.trim();
      const updated = unwrap(
        await scoreApi.shows.setShowFriendlyName(showId, {
          friendlyName: trimmed === "" ? null : trimmed,
        })
      );
      setValue(updated.friendlyName ?? "");
      // Push the saved value into the shared active-show list so the app bar
      // switcher updates without a reload.
      onSaved?.(showId, { friendlyName: updated.friendlyName ?? null });
      setFeedback({
        severity: "success",
        message: updated.friendlyName
          ? t("settings.friendlyName.saveSuccess")
          : t("settings.friendlyName.cleared"),
      });
    } catch {
      setFeedback({ severity: "error", message: t("settings.friendlyName.saveError") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" data-testid="show-friendly-name-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.friendlyName.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.friendlyName.help")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <TextField
            size="small"
            label={t("settings.friendlyName.label")}
            value={value}
            disabled={saving}
            onChange={(e) => setValue(e.target.value)}
            helperText=" "
            inputProps={{
              "data-testid": "show-friendly-name-input",
              maxLength: MAX_SHOW_FRIENDLY_NAME_LENGTH,
            }}
          />
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            data-testid="show-friendly-name-save-button"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t("settings.friendlyName.save")}
          </Button>
        </Stack>
        <Snackbar
          open={feedback != null}
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={
                feedback.severity === "success"
                  ? "show-friendly-name-success"
                  : "show-friendly-name-error"
              }
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// #3295 - what this show calls the sub-groups of an Equitation / Medal class
// ("Section", "Heat", "Group") and whether they are numbered 1, 2, 3 or lettered
// A, B, C (score-api's setShowSectionSettings, Show.sectionLabel /
// Show.sectionNumbering). Display only: the stored ride.subsection stays an
// integer. The live example runs through score-common's one formatSection, the
// same function every other surface uses. A blank name means the default word,
// which is a translation key (the show's own word is data, shown as typed).
function SectionSettingsCard({ showId, initialLabel, initialNumbering, scoreApi, onSaved, t }) {
  const [label, setLabel] = useState(initialLabel ?? "");
  const [numbering, setNumbering] = useState(normalizeSectionNumbering(initialNumbering));
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  // Same rule as FriendlyNameCard: a save pushes the stored value back through
  // the props, which must not clear the success toast it just raised.
  useEffect(() => {
    setLabel(initialLabel ?? "");
  }, [initialLabel]);
  useEffect(() => {
    setNumbering(normalizeSectionNumbering(initialNumbering));
  }, [initialNumbering]);
  useEffect(() => {
    setFeedback(null);
  }, [showId]);

  const example = formatSection(2, {
    label: normalizeSectionLabel(label) ?? t("settings.sectionSettings.defaultLabel"),
    numbering,
  });

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const trimmed = label.trim();
      const updated = unwrap(
        await scoreApi.shows.setShowSectionSettings(showId, {
          sectionLabel: trimmed === "" ? null : trimmed,
          sectionNumbering: numbering,
        })
      );
      setLabel(updated.sectionLabel ?? "");
      setNumbering(normalizeSectionNumbering(updated.sectionNumbering));
      onSaved?.(showId, {
        sectionLabel: updated.sectionLabel ?? null,
        sectionNumbering: updated.sectionNumbering ?? numbering,
      });
      setFeedback({ severity: "success", message: t("settings.sectionSettings.saveSuccess") });
    } catch {
      setFeedback({ severity: "error", message: t("settings.sectionSettings.saveError") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" id="show-section-settings-card" data-testid="show-section-settings-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.sectionSettings.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.sectionSettings.help")}
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <TextField
            size="small"
            label={t("settings.sectionSettings.label")}
            placeholder={t("settings.sectionSettings.defaultLabel")}
            value={label}
            disabled={saving}
            onChange={(e) => setLabel(e.target.value)}
            inputProps={{
              "data-testid": "show-section-label-input",
              maxLength: MAX_SECTION_LABEL_LENGTH,
            }}
          />
          <FormControl disabled={saving}>
            <FormLabel id="show-section-numbering-label">
              {t("settings.sectionSettings.numberingLabel")}
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="show-section-numbering-label"
              value={numbering}
              onChange={(e) => setNumbering(e.target.value)}
            >
              <FormControlLabel
                value="numeric"
                control={<Radio inputProps={{ "data-testid": "show-section-numbering-numeric" }} />}
                label={t("settings.sectionSettings.numbers")}
              />
              <FormControlLabel
                value="alpha"
                control={<Radio inputProps={{ "data-testid": "show-section-numbering-alpha" }} />}
                label={t("settings.sectionSettings.letters")}
              />
            </RadioGroup>
          </FormControl>
          <Typography variant="body1" color="text.primary" data-testid="show-section-example">
            {t("settings.sectionSettings.example")}: <strong>{example}</strong>
          </Typography>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            data-testid="show-section-save-button"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t("settings.sectionSettings.save")}
          </Button>
        </Stack>
        <Snackbar
          open={feedback != null}
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={
                feedback.severity === "success" ? "show-section-success" : "show-section-error"
              }
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// Card #3611 - one setting for the class sponsor, the class prize and the
// sponsor logo on the Final Class / Mid-Class report and the competitor
// scoresheet (score-api's setShowSponsorPrizeSettings, Show.sponsorPrizeMode /
// Show.sponsorPrizeShow). Per show = one set of switches for every class here;
// per class = each class carries its own switches (set on the class's detail
// drawer); off = none of the three print. Nothing stored reads as per show with
// everything on, so reports do not change until staff change it.
function SponsorPrizeCard({ showId, initialMode, initialSwitches, scoreApi, onSaved, t }) {
  const [mode, setMode] = useState(sponsorPrizeModeOf({ sponsorPrizeMode: initialMode }));
  const [switches, setSwitches] = useState(normalizeSponsorPrizeSwitches(initialSwitches));
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  useEffect(() => {
    setMode(sponsorPrizeModeOf({ sponsorPrizeMode: initialMode }));
  }, [initialMode]);
  useEffect(() => {
    setSwitches(normalizeSponsorPrizeSwitches(initialSwitches));
  }, [initialSwitches]);
  useEffect(() => {
    setFeedback(null);
  }, [showId]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const updated = unwrap(
        await scoreApi.shows.setShowSponsorPrizeSettings(showId, { mode, switches })
      );
      onSaved?.(showId, {
        sponsorPrizeMode: updated.sponsorPrizeMode ?? mode,
        sponsorPrizeShow: updated.sponsorPrizeShow ?? switches,
      });
      setFeedback({ severity: "success", message: t("settings.sponsorPrize.saveSuccess") });
    } catch {
      setFeedback({ severity: "error", message: t("settings.sponsorPrize.saveError") });
    } finally {
      setSaving(false);
    }
  };

  const switchRow = (key, label) => (
    <FormControlLabel
      key={key}
      control={
        <Checkbox
          checked={switches[key]}
          disabled={saving}
          onChange={(e) => setSwitches((prev) => ({ ...prev, [key]: e.target.checked }))}
          inputProps={{ "data-testid": `sponsor-prize-switch-${key}` }}
        />
      }
      label={label}
    />
  );

  return (
    <Card variant="outlined" id="sponsor-prize-card" data-testid="sponsor-prize-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.sponsorPrize.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.sponsorPrize.help")}
        </Typography>
        <Stack spacing={1} sx={{ mt: 1 }} alignItems="flex-start">
          <FormControl disabled={saving}>
            <FormLabel id="sponsor-prize-mode-label">{t("settings.sponsorPrize.modeLabel")}</FormLabel>
            <RadioGroup
              aria-labelledby="sponsor-prize-mode-label"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <FormControlLabel
                value="show"
                control={<Radio inputProps={{ "data-testid": "sponsor-prize-mode-show" }} />}
                label={t("settings.sponsorPrize.modeShow")}
              />
              <FormControlLabel
                value="class"
                control={<Radio inputProps={{ "data-testid": "sponsor-prize-mode-class" }} />}
                label={t("settings.sponsorPrize.modeClass")}
              />
              <FormControlLabel
                value="off"
                control={<Radio inputProps={{ "data-testid": "sponsor-prize-mode-off" }} />}
                label={t("settings.sponsorPrize.modeOff")}
              />
            </RadioGroup>
          </FormControl>
          {mode === "show" && (
            <Box sx={{ pl: 3, display: "flex", flexDirection: "column" }} data-testid="sponsor-prize-switches">
              {switchRow("sponsor", t("settings.sponsorPrize.sponsor"))}
              {switchRow("prize", t("settings.sponsorPrize.prize"))}
              {switchRow("sponsorLogo", t("settings.sponsorPrize.sponsorLogo"))}
            </Box>
          )}
          {mode === "class" && (
            <Typography variant="body2" color="text.secondary" sx={{ pl: 3 }} data-testid="sponsor-prize-class-note">
              {t("settings.sponsorPrize.classNote")}
            </Typography>
          )}
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            data-testid="sponsor-prize-save-button"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t("settings.sponsorPrize.save")}
          </Button>
        </Stack>
        <Snackbar
          open={feedback != null}
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={
                feedback.severity === "success" ? "sponsor-prize-success" : "sponsor-prize-error"
              }
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// #3263 - per-show on/off switch for backend auto-placing (score-api's
// setShowAutoPlaceClasses, same RMW pattern/home as NoFoxboxCard above).
// Replaces #2000's default-placing-view setting. Stored as the boolean
// Show.autoPlaceClasses; the backend auto-placer reads it as
// `autoPlaceClasses !== false`, so a show with no stored value is ON and this
// switch must render ON for it (`initialValue !== false`, never `=== true`,
// which is NoFoxboxCard's opposite-default polarity). It is the live-show
// escape hatch: switch it off and classes are only placed by hand.
function AutoPlaceClassesCard({ showId, initialValue, scoreApi, onSaved, t }) {
  const [checked, setChecked] = useState(initialValue !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setChecked(initialValue !== false);
    setError(null);
  }, [showId, initialValue]);

  const handleChange = async (event) => {
    const next = event.target.checked;
    const previous = checked;
    setSaving(true);
    setError(null);
    setChecked(next); // optimistic; reverts on failure below
    try {
      const updated = unwrap(
        await scoreApi.shows.setShowAutoPlaceClasses(showId, { autoPlaceClasses: next })
      );
      const saved = updated.autoPlaceClasses !== false;
      setChecked(saved);
      onSaved?.(showId, { autoPlaceClasses: saved });
    } catch (err) {
      setChecked(previous);
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" id="auto-place-classes-card" data-testid="auto-place-classes-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.autoPlaceClasses.title")}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              disabled={saving}
              onChange={handleChange}
              data-testid="auto-place-classes-toggle"
              inputProps={{ "aria-label": t("settings.autoPlaceClasses.label") }}
            />
          }
          label={t("settings.autoPlaceClasses.label")}
        />
        <Typography variant="body2" color="text.secondary">
          {t("settings.autoPlaceClasses.help")}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="auto-place-classes-error">
            {t("settings.autoPlaceClasses.saveError")}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// #3561 - per-show Instant updates switch (score-api's setShowInstantUpdates).
// Stored as Show.instantUpdates; the backend reads it as `=== true`, so a show
// with no stored value is OFF and this switch renders OFF for it (the opposite
// polarity of AutoPlaceClassesCard). Off is never broken: staff screens fall
// back to the change check and still update, a few seconds slower. The route
// requires score:assign_scribes, so the card only renders for a user who holds
// it (see the caller).
function InstantUpdatesCard({ showId, initialValue, scoreApi, onSaved, t }) {
  const [checked, setChecked] = useState(initialValue === true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setChecked(initialValue === true);
    setError(null);
  }, [showId, initialValue]);

  const handleChange = async (event) => {
    const next = event.target.checked;
    const previous = checked;
    setSaving(true);
    setError(null);
    setChecked(next);
    try {
      const updated = unwrap(
        await scoreApi.shows.setShowInstantUpdates(showId, { instantUpdates: next })
      );
      const saved = updated.instantUpdates === true;
      setChecked(saved);
      onSaved?.(showId, { instantUpdates: saved });
    } catch (err) {
      setChecked(previous);
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" data-testid="instant-updates-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.instantUpdates.title")}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              disabled={saving}
              onChange={handleChange}
              data-testid="instant-updates-toggle"
              inputProps={{ "aria-label": t("settings.instantUpdates.label") }}
            />
          }
          label={t("settings.instantUpdates.label")}
        />
        <Typography variant="body2" color="text.secondary">
          {t("settings.instantUpdates.help")}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="instant-updates-error">
            {t("settings.instantUpdates.saveError")}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

const LOGO_ACCEPT = "image/png,image/gif,image/jpeg";
const LOGO_MIN_LONG_EDGE = 600;
const LOGO_ALLOWED_TYPES = ["image/png", "image/gif", "image/jpeg"];
// Mirrors score-api's lib/logo.js LOGO_MAX_BYTES — the real ceiling is AWS
// Lambda's ~6MB synchronous invoke payload limit (a base64 body between the
// server's raw cap and that limit dies as an opaque 502/413 upstream of any
// server-side validation), so this client check is defense in depth for the
// common case, NOT the only guard — the server enforces the same cap
// independently and is what actually protects the route.
const LOGO_MAX_BYTES = 4 * 1024 * 1024;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("could not read file"));
    reader.readAsDataURL(file);
  });
}

function loadImageDimensions(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("could not read image"));
    img.src = dataUrl;
  });
}

// #1058 — per-show logo (ShowConfig #351's second consumer, alongside
// ScribePinCard above). Uploaded here as a base64 data URL and persisted on
// the Show row as `logoKey` (score-api's setShowLogo, same RMW pattern/home
// as NoFoxboxCard/ScribePinCard) — an app-owned field that survives every
// FVD re-reconcile the same way. PNG/GIF/JPEG only, minimum 600px on the
// long edge so it still looks decent shrunk to fit a report footer: checked
// here client-side for instant feedback AND server-side (the real gate — a
// client check is only ever a UX nicety, never trustworthy on its own). The
// uploaded image lives in its own private S3 bucket; this page only ever
// sees a short-lived presigned `logoUrl` the server mints fresh on every
// read (activeShow/listShows), never a permanent public link.
function LogoCard({ showId, initialLogoUrl, scoreApi, t }) {
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl ?? null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  // Re-sync to the selected show ONLY when it actually changes — skip the
  // initial mount. React flushes passive effects after paint, so a mount
  // effect that unconditionally `setFeedback(null)` can run *after* an early
  // file pick already set a validation error, wiping the toast before it's
  // seen — the source of the #1126 Cypress flake. On mount, logoUrl is already
  // seeded from the prop and feedback is already null, so there's nothing to
  // reset; only a genuine show switch needs to clear stale logo/feedback.
  const prevShowRef = useRef({ showId, initialLogoUrl });
  useEffect(() => {
    const prev = prevShowRef.current;
    if (prev.showId === showId && prev.initialLogoUrl === initialLogoUrl) return;
    prevShowRef.current = { showId, initialLogoUrl };
    setLogoUrl(initialLogoUrl ?? null);
    setFeedback(null);
  }, [showId, initialLogoUrl]);

  const applyLogo = async (image) => {
    setSaving(true);
    setFeedback(null);
    try {
      const updated = unwrap(await scoreApi.shows.setShowLogo(showId, { image }));
      setLogoUrl(updated.logoUrl ?? null);
      setFeedback({
        severity: "success",
        message: image ? t("settings.logo.saveSuccess") : t("settings.logo.removeSuccess"),
      });
    } catch (err) {
      const code = err?.response?.data?.code;
      const message =
        code === "logo_too_small"
          ? t("settings.logo.errorTooSmall")
          : code === "logo_too_large"
            ? t("settings.logo.errorTooLarge")
            : code === "invalid_image_type"
              ? t("settings.logo.errorInvalidType")
              : t("settings.logo.errorGeneric");
      setFeedback({ severity: "error", message });
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;

    if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
      setFeedback({ severity: "error", message: t("settings.logo.errorInvalidType") });
      return;
    }

    if (file.size > LOGO_MAX_BYTES) {
      setFeedback({ severity: "error", message: t("settings.logo.errorTooLarge") });
      return;
    }

    setSaving(true);
    setFeedback(null);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const { width, height } = await loadImageDimensions(dataUrl);
      if (Math.max(width, height) < LOGO_MIN_LONG_EDGE) {
        setFeedback({ severity: "error", message: t("settings.logo.errorTooSmall") });
        setSaving(false);
        return;
      }
      await applyLogo(dataUrl);
    } catch {
      setFeedback({ severity: "error", message: t("settings.logo.errorGeneric") });
      setSaving(false);
    }
  };

  const handleRemove = () => applyLogo(null);

  return (
    <Card variant="outlined" data-testid="show-logo-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.logo.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.logo.help")}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mt: 1 }}
          alignItems="center"
        >
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={t("settings.logo.currentAlt")}
              data-testid="show-logo-preview"
              sx={{
                maxHeight: 80,
                maxWidth: 200,
                objectFit: "contain",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                p: 0.5,
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" data-testid="show-logo-empty">
              {t("settings.logo.noneUploaded")}
            </Typography>
          )}

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              component="label"
              disabled={saving}
              data-testid="show-logo-upload-button"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {logoUrl ? t("settings.logo.replaceButton") : t("settings.logo.uploadButton")}
              <input
                type="file"
                accept={LOGO_ACCEPT}
                hidden
                onChange={handleFileChange}
                data-testid="show-logo-file-input"
              />
            </Button>
            {logoUrl && (
              <Button
                variant="outlined"
                color="error"
                disabled={saving}
                onClick={handleRemove}
                data-testid="show-logo-remove-button"
              >
                {t("settings.logo.removeButton")}
              </Button>
            )}
          </Stack>
        </Stack>

        <Snackbar
          open={feedback != null}
          // Success auto-dismisses; an error stays until the user closes it —
          // a validation error the user never got to read is worse than a
          // lingering toast, and the 6s auto-hide also raced Cypress under CI
          // load (the error toast vanished before the assertion polled it),
          // flaking the prod deploy. #1126.
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={feedback.severity === "success" ? "show-logo-success" : "show-logo-error"}
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// #2379 — the demo org has exactly one scribe role.
const SCRIBE_ROLE = "score:score_scribe";

// #1029 — demo Re-seed Show control, wired to #1028's POST /demo/reseed.
// LIVE=false only (see util/live.js's isLive header comment for why the
// backend is the real authority and this client gate is UX-only): the
// endpoint itself hard-refuses with 403 live_guard when LIVE=true and 403
// prod_refused on ENVIRONMENT_NAME=main, so this control simply isn't worth
// showing where it can never succeed.
//
// #2379 rework: the three baked-dump `state` presets and the client-supplied
// `startDate` are GONE from the contract (Jaybill's decision: one seed only,
// the start date is pulled server-side off the already-ingested show's own
// startDate) — there is nothing left here for a dropdown or a date picker to
// control, so both are removed rather than left as dead controls that don't
// reach the server.
//
// scribeUserIds is new and is this card's headline capability: score-api's
// Lambda role only reaches score-* tables (see lib/demo-scribe-assignments.js
// header), so it cannot look up org-demo's membership itself. scoreadmin-fe
// already has account-api access via useOrgMembers, so THIS is where the
// scribe list comes from — never inferred from an email domain (a demo user
// can sit on a different org's real UUID), always the active org's real
// membership, filtered to the scribe role.
//
// Exported only for Settings.reseedRefetch.test.jsx (#2857), which renders it
// directly to reach the token-renew refetch window Cypress cannot.
export function ReseedShowCard({ scoreApi, t }) {
  const { activeOrgId } = useActiveOrg();
  const { members: orgMembers, loading: membersLoading } = useOrgMembers(activeOrgId);
  const scribes = orgMembers.filter((m) => (m.roles || []).includes(SCRIBE_ROLE));

  const [scribeUserIds, setScribeUserIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message } | null

  // Default to "assign every available scribe" once the org member list
  // loads, so the common case (reseed + assign, one click) needs no manual
  // selection — the multi-select below still lets an operator narrow it.
  const scribeIdsKey = scribes.map((m) => m.userId).join(",");
  useEffect(() => {
    setScribeUserIds(scribes.map((m) => m.userId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scribeIdsKey]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = unwrap(
        await scoreApi.demo.reseedDemo({
          scribeUserIds,
        })
      );
      setFeedback({
        severity: "success",
        message: t("settings.reseed.success", {
          classesMerged: result.applied?.classesMerged ?? 0,
          ridesMerged: result.applied?.ridesMerged ?? 0,
          assignmentCount: result.scribes?.assignmentCount ?? 0,
        }),
      });
    } catch (err) {
      setFeedback({ severity: "error", message: err.message || t("settings.reseed.error") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card variant="outlined" data-testid="reseed-show-card">
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {t("settings.reseed.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("settings.reseed.help")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="reseed-scribes-label">{t("settings.reseed.scribesLabel")}</InputLabel>
            <Select
              labelId="reseed-scribes-label"
              label={t("settings.reseed.scribesLabel")}
              multiple
              value={scribeUserIds}
              disabled={submitting || membersLoading}
              onChange={(event) => setScribeUserIds(event.target.value)}
              data-testid="reseed-scribes-select"
              renderValue={(selected) =>
                scribes
                  .filter((m) => selected.includes(m.userId))
                  .map((m) => userDisplayName(m))
                  .join(", ")
              }
            >
              {scribes.map((m) => (
                <MenuItem key={m.userId} value={m.userId}>
                  <Checkbox checked={scribeUserIds.includes(m.userId)} />
                  <ListItemText primary={userDisplayName(m)} />
                </MenuItem>
              ))}
            </Select>
            {!membersLoading && scribes.length === 0 ? (
              <FormHelperText data-testid="reseed-no-scribes">{t("settings.reseed.noScribes")}</FormHelperText>
            ) : null}
          </FormControl>

          <Button
            variant="contained"
            disabled={submitting || membersLoading || scribeUserIds.length === 0}
            onClick={handleSubmit}
            data-testid="reseed-submit-button"
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t("settings.reseed.submit")}
          </Button>
        </Stack>

        <Snackbar
          open={feedback != null}
          // Success auto-dismisses; an error stays until the user closes it —
          // a validation error the user never got to read is worse than a
          // lingering toast, and the 6s auto-hide also raced Cypress under CI
          // load (the error toast vanished before the assertion polled it),
          // flaking the prod deploy. #1126.
          autoHideDuration={feedback?.severity === "error" ? null : 6000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {feedback ? (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              data-testid={feedback.severity === "success" ? "reseed-success" : "reseed-error"}
              sx={{ width: "100%" }}
            >
              {feedback.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// #3653: one width and one gap for every card on this page, shared by the
// settings-content Stack and the Re-seed Show wrapper (which sits outside that
// Stack on purpose, see the #2379 round 8 note at the bottom of SettingsPage).
const SETTINGS_MAX_WIDTH = 720;
const SETTINGS_CARD_SPACING = 3;

export default function SettingsPage() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.settings"));
  const { activeShowId, activeShow, patchShow, loading: loadingShows } = useActiveShow();
  const canAssignScribes = useCanAssignScribes();
  const scoreApi = useScoreApi();
  // #3570 -- the Pre-show dashboard links to /settings#<card id>: scroll to that
  // card once the page has rendered it (ADR 0004, a link to the exact place).
  const { hash } = useLocation();
  const ready = !loadingShows && Boolean(activeShowId);
  useEffect(() => {
    if (!ready || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView?.({ block: "start" });
  }, [ready, hash]);

  const na = t("settings.notAvailable");

  // MD3 loading convention (#514): shaped skeleton, not a blank flash.
  if (loadingShows) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom color="text.primary">
          {t("settings.title")}
        </Typography>
        <TableSkeleton rows={1} columns={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("settings.title")}
      </Typography>

      {!activeShowId && (
        <Typography color="text.secondary">{t("settings.noShows")}</Typography>
      )}

      {activeShowId && (
        <Stack spacing={SETTINGS_CARD_SPACING} data-testid="settings-content" sx={{ maxWidth: SETTINGS_MAX_WIDTH }}>
          <InfoSection title={t("settings.sections.showInfo")} testId="settings-show-info">
            <InfoRow
              label={t("settings.fields.name")}
              value={activeShow?.name ?? na}
              testId="settings-field-name"
            />
            <InfoRow
              label={t("settings.fields.organization")}
              value={activeShow?.organization ?? na}
              testId="settings-field-organization"
            />
            <InfoRow
              label={t("settings.fields.location")}
              value={activeShow?.location ?? na}
              testId="settings-field-location"
            />
            <InfoRow
              label={t("settings.fields.startDate")}
              value={formatShowDate(activeShow?.startDate) ?? na}
              testId="settings-field-start-date"
            />
            <InfoRow
              label={t("settings.fields.endDate")}
              value={formatShowDate(activeShow?.endDate) ?? na}
              testId="settings-field-end-date"
            />
          </InfoSection>

          <InfoSection title={t("settings.sections.usef")} testId="settings-usef">
            <InfoRow
              label={t("settings.fields.usefShowId")}
              value={activeShow?.usefCompId ?? na}
              testId="settings-field-usef-show-id"
            />
            <InfoRow
              label={t("settings.fields.usefCompYear")}
              value={activeShow?.usefCompYear ?? na}
              testId="settings-field-usef-comp-year"
            />
          </InfoSection>

          <InfoSection title={t("settings.sections.management")} testId="settings-management">
            <InfoRow
              label={t("settings.fields.manager")}
              value={activeShow?.managerName ?? na}
              testId="settings-field-manager"
            />
            <InfoRow
              label={t("settings.fields.secretary")}
              value={activeShow?.secretaryName ?? na}
              testId="settings-field-secretary"
            />
          </InfoSection>

          <RingsAndDaysSection showId={activeShowId} scoreApi={scoreApi} />

          <LogoCard
            showId={activeShowId}
            initialLogoUrl={activeShow?.logoUrl}
            scoreApi={scoreApi}
            t={t}
          />

          {/* #3630 -- Re-run sync lives here (config), not on the dashboard. The
              dashboard's sync Alert links to this card by its id. */}
          <Card variant="outlined" id={FVD_SYNC_CARD_ID} data-testid="fvd-sync-card">
            <CardContent>
              <Typography variant="h6" gutterBottom color="text.primary">
                {t("dashboard.reconcile.title")}
              </Typography>
              <ReconcileStatusSection showId={activeShowId} />
            </CardContent>
          </Card>

          <NoFoxboxCard
            showId={activeShowId}
            initialValue={activeShow?.noFoxbox}
            scoreApi={scoreApi}
            t={t}
          />

          <FriendlyNameCard
            showId={activeShowId}
            initialValue={activeShow?.friendlyName}
            scoreApi={scoreApi}
            onSaved={patchShow}
            t={t}
          />

          <SectionSettingsCard
            showId={activeShowId}
            initialLabel={activeShow?.sectionLabel}
            initialNumbering={activeShow?.sectionNumbering}
            scoreApi={scoreApi}
            onSaved={patchShow}
            t={t}
          />

          <SponsorPrizeCard
            showId={activeShowId}
            initialMode={activeShow?.sponsorPrizeMode}
            initialSwitches={activeShow?.sponsorPrizeShow}
            scoreApi={scoreApi}
            onSaved={patchShow}
            t={t}
          />

          <ScribePinCard
            showId={activeShowId}
            initialValue={activeShow?.scribeUnlockPin}
            scoreApi={scoreApi}
            t={t}
          />

          <AutoPlaceClassesCard
            showId={activeShowId}
            initialValue={activeShow?.autoPlaceClasses}
            scoreApi={scoreApi}
            onSaved={patchShow}
            t={t}
          />

          {canAssignScribes && (
            <InstantUpdatesCard
              showId={activeShowId}
              initialValue={activeShow?.instantUpdates}
              scoreApi={scoreApi}
              onSaved={patchShow}
              t={t}
            />
          )}
        </Stack>
      )}

      {/* card #2379 round 8: was previously the last child of the
          `activeShowId &&` Stack above, so it could never render on a
          genuinely empty org (zero shows) -- exactly the state reseed exists
          to fix from scratch. Every existing spec's GET /show intercept
          always returns one show, so this never went red locally; it only
          surfaced hitting the real, freshly-deployed (zero-show) demo env.
          Moved outside both the activeShowId and !activeShowId branches so
          it renders in either state, still gated on !isLive() only. */}
      {/* #3653: same width and gap as the settings-content Stack above, so
          the card lines up with the ones over it. */}
      {!isLive() && (
        <Box data-testid="reseed-show-container" sx={{ maxWidth: SETTINGS_MAX_WIDTH, mt: SETTINGS_CARD_SPACING }}>
          <ReseedShowCard scoreApi={scoreApi} t={t} />
        </Box>
      )}
    </Box>
  );
}
